/**
 * The protagonist. Special-cased rendering for the center node
 * (default `submitted`).
 *
 * - Larger sphere than the instanced hub mesh, slightly warm so it reads as
 *   the focal point against the cool blue-gray network.
 * - Two concentric halo rings, slowly counter-rotating.
 * - Soft outer corona via a back-side billboard plane with an additive
 *   radial gradient.
 * - Always-on label with thin elegant typography.
 *
 * Inspiration: Kirell Benzi's "central character" treatment in the Star Wars
 * galaxy and GoT character networks — a single visibly different anchor that
 * the eye lands on first.
 */
import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { CENTER_COLOR, CENTER_HALO } from '../zones/palette';
import { useAppStore } from '../state/store';
import { dragState } from '../state/drag';
import type { Graph } from '../types/domain';
import { CENTER_RADIUS } from './sizing';
import type { ForceLayout } from './useForceLayout';


// World-space label offset above the protagonist. Halo ring 2 extends to
// CENTER_RADIUS * 2.1 ≈ 2.2; add headroom so the label is unmistakably
// outside both rings at any zoom.
const CENTER_LABEL_OFFSET = 2.55;
const TMP_UP = new THREE.Vector3();

type Props = {
  graph: Graph;
  layout: ForceLayout;
  centerId: string;
  glowNodes: Set<string>;
};

const DIM_FACTOR = 0.05;
const DIM_LABEL_OPACITY = '0.08';
const BASE_LABEL_OPACITY = '0.85';

export function CenterAnchor({ graph, layout, centerId, glowNodes }: Props) {
  const groupRef = useRef<THREE.Group>(null!);
  const sphereRef = useRef<THREE.Mesh>(null!);
  const ring1 = useRef<THREE.Mesh>(null!);
  const ring2 = useRef<THREE.Mesh>(null!);
  const labelInnerRef = useRef<THREE.Group>(null!);
  const labelRef = useRef<HTMLDivElement>(null!);

  const setSelection = useAppStore((s) => s.setSelection);
  const setHover = useAppStore((s) => s.setHover);
  const clearHoverSoon = useAppStore((s) => s.clearHoverSoon);
  const node = graph.byId.get(centerId);

  // Dim the protagonist when an active set exists and doesn't include it.
  // Unified dim — same factor for hover and select.
  const isDimmed = glowNodes.size > 0 && !glowNodes.has(centerId);

  useFrame(({ clock, camera }) => {
    if (!layout.posRef.current || !groupRef.current) return;
    const idx = layout.nodeIndex.get(centerId);
    if (idx === undefined) return;
    const pos = layout.posRef.current;
    groupRef.current.position.set(pos[idx * 3], pos[idx * 3 + 1], pos[idx * 3 + 2]);

    if (ring1.current) {
      ring1.current.lookAt(camera.position);
      ring1.current.rotation.z = clock.elapsedTime * 0.12;
    }
    if (ring2.current) {
      ring2.current.lookAt(camera.position);
      ring2.current.rotation.z = -clock.elapsedTime * 0.08;
    }
    if (sphereRef.current) {
      const s = 1 + Math.sin(clock.elapsedTime * 0.9) * 0.04;
      sphereRef.current.scale.setScalar(s);
    }

    // Label inner group sits at world cameraScreenUp * offset above the
    // anchor, so it always hugs the outer halo ring regardless of zoom.
    if (labelInnerRef.current) {
      TMP_UP.set(0, 1, 0).applyQuaternion(camera.quaternion);
      labelInnerRef.current.position.set(
        TMP_UP.x * CENTER_LABEL_OFFSET,
        TMP_UP.y * CENTER_LABEL_OFFSET,
        TMP_UP.z * CENTER_LABEL_OFFSET,
      );
    }

    // Dim sphere material color when not in active set. Cached __dim
    // sentinel avoids re-tinting every frame in the steady state.
    if (sphereRef.current) {
      const mat = sphereRef.current.material as THREE.MeshBasicMaterial;
      const applied = isDimmed ? DIM_FACTOR : 1;
      const tagged = mat as { __dimApplied?: number };
      if (mat && tagged.__dimApplied !== applied) {
        mat.color.set(CENTER_COLOR).multiplyScalar(applied);
        tagged.__dimApplied = applied;
      }
    }
    if (labelRef.current) {
      labelRef.current.style.opacity = isDimmed ? DIM_LABEL_OPACITY : BASE_LABEL_OPACITY;
    }
  });

  if (!node) return null;

  return (
    <group ref={groupRef}>
      {/* Outer halo plane (soft corona) */}
      <mesh raycast={() => null}>
        <circleGeometry args={[CENTER_RADIUS * 4.5, 64]} />
        <meshBasicMaterial
          color={CENTER_HALO}
          transparent
          opacity={0.05}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          toneMapped={false}
        />
      </mesh>

      {/* Concentric rings */}
      <mesh ref={ring1} raycast={() => null}>
        <ringGeometry args={[CENTER_RADIUS * 1.55, CENTER_RADIUS * 1.62, 64]} />
        <meshBasicMaterial
          color={CENTER_HALO}
          transparent
          opacity={0.55}
          side={THREE.DoubleSide}
          toneMapped={false}
        />
      </mesh>
      <mesh ref={ring2} raycast={() => null}>
        <ringGeometry args={[CENTER_RADIUS * 2.05, CENTER_RADIUS * 2.1, 64]} />
        <meshBasicMaterial
          color={CENTER_HALO}
          transparent
          opacity={0.28}
          side={THREE.DoubleSide}
          toneMapped={false}
        />
      </mesh>

      {/* Center sphere — clickable */}
      <mesh
        ref={sphereRef}
        onClick={(e) => {
          // Don't fire on a drag release (matches Nodes.tsx).
          if (dragState.isDragging) return;
          e.stopPropagation();
          // Center can be a position (default) OR a move (in
          // moves-as-nodes when the configured fallback resolves to a
          // move). Pick the selection kind from the underlying graph
          // node so the drawer gets a routable selection — hardcoding
          // 'position' caused move-centers to open an empty drawer.
          const centerKind = node.kind === 'move' ? 'move' : 'position';
          const state = useAppStore.getState();
          // Re-clicking the already-selected centre is a no-op — see
          // Nodes.tsx; the in-scene card it used to toggle is gone.
          if (
            state.selection.kind === centerKind &&
            state.selection.id === centerId
          ) {
            return;
          }
          setSelection({ kind: centerKind, id: centerId });
        }}
        onPointerOver={(e) => {
          // Honor the global press gate so a click-drag pan over the
          // center sphere doesn't flash a hover state.
          if (dragState.isPressed) return;
          e.stopPropagation();
          setHover(centerId);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={(e) => {
          e.stopPropagation();
          clearHoverSoon();
          document.body.style.cursor = 'default';
        }}
      >
        <sphereGeometry args={[CENTER_RADIUS, 32, 24]} />
        <meshBasicMaterial color={CENTER_COLOR} toneMapped={false} />
      </mesh>

      {/* Label — inner group is offset along screen-up in world space,
          so the label hugs the halo rings at any zoom level. */}
      <group ref={labelInnerRef}>
        <Html
          position={[0, 0, 0]}
          zIndexRange={[100, 0]}
          style={{ pointerEvents: 'none' }}
        >
          <div
            ref={labelRef}
            style={{
              color: '#e6dfcf',
              fontSize: 11,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              fontWeight: 300,
              textShadow: '0 0 8px rgba(0,0,0,0.9)',
              whiteSpace: 'nowrap',
              opacity: 0.85,
              transition: 'opacity 160ms ease',
              transform: 'translate(-50%, calc(-100% - 4px))',
              textAlign: 'center',
              userSelect: 'none',
            }}
          >
            {node.label || 'Standing'}
          </div>
        </Html>
      </group>
    </group>
  );
}
